import { Graph, Node, Link, LinkStrength, AnchorType, LinkType, Anchor, Spawn } from "@livereader/graphly-d3";
import type {
	TemplateProcess,
	TemplateTask,
	TemplateEvent,
	ConceptEvent,
	ConceptDataobject,
} from "@lr-notitia/notitia-designer/schemas";
import { useTemplateProcessesStore } from "../../../store/t-process";
import { useGraphStore } from "../../../store/graph";
import { useTeamlyStore } from "../../../store/teamly";
import { useGraphlyTemplatesStore } from "../../../store/graphly-templates";
import { useSymbolsStore } from "../../../store/symbols";
import markdownToTxt from "markdown-to-txt";

let processesStore: ReturnType<typeof useTemplateProcessesStore>;
let graphStore: ReturnType<typeof useGraphStore>;
let teamlyStore: ReturnType<typeof useTeamlyStore>;
let graphlyTemplatesStore: ReturnType<typeof useGraphlyTemplatesStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;

function init() {
	processesStore = useTemplateProcessesStore();
	graphStore = useGraphStore();
	teamlyStore = useTeamlyStore();
	graphlyTemplatesStore = useGraphlyTemplatesStore();
	symbolsStore = useSymbolsStore();
}

export function graph(processId: string): Graph {
	if (!processesStore || !graphStore || !teamlyStore || !graphlyTemplatesStore || !symbolsStore) init();
	const process: TemplateProcess | undefined = processesStore.getById(processId);
	if (!process) return { nodes: [], links: [] };
	const nodes: Node[] = [];
	const links: Link[] = [];

	const events = [...process.events];
	events
		.map((e) => graphStore.template.event.byId(e))
		.filter((e) => e !== undefined)
		.forEach((e) => {
			let concept: ConceptEvent | undefined = graphStore.concept.event.byId(e?.concept || "");
			if (!concept) return;
			nodes.push(eventNode(e as TemplateEvent, process));
			let conceptLabel = symbolsStore.term({
				id: concept?.refersTo || "",
				terminology: concept?.terminology || {},
			});
			e?.activates.forEach((id) => {
				let link = createActivatesLink(e.id, id, conceptLabel);
				if (link) links.push(link);
			});
		});

	const tasks = [...process.tasks].map((t) => graphStore.template.task.byId(t)).filter((t) => t !== undefined);
	tasks.forEach((task) => {
		if (task?.concept === "condition_task") nodes.push(conditionTaskNode(task as TemplateTask, process));
		else if (task?.concept.includes("logic_gate")) nodes.push(logicGateNode(task as TemplateTask, process));
		else nodes.push(taskNode(task as TemplateTask, process));
		// activates links
		task?.activates.forEach((id) => {
			// if event is activated, change the label, else write "activates"
			let event =
				graphStore.template.event.byId(id)?.type === "event" ? graphStore.template.event.byId(id) : undefined;
			let conceptEvent = event ? graphStore.concept.event.byId(event?.concept || "") : undefined;
			let label = conceptEvent
				? symbolsStore.term({ id: conceptEvent?.refersTo || "", terminology: conceptEvent?.terminology || {} })
				: "";
			let link = createActivatesLink(task.id, id, label);
			if (!link) return;
			if (graphStore.template.task.isDeactivated(task.id)) {
				link.type = LinkType.Dashed;
				link.label = "inactive";
			}
			links.push(link);
		});
	});
	// matching input & output links
	tasks.forEach((task) => {
		if (!processesStore.matchesLinkVisibility) return;
		if (!task?.outputAttributes || task.outputAttributes.length === 0) return;
		tasks.forEach((t) => {
			if (t?.id === task.id) return;
			if (t?.inputAttributes && t?.inputAttributes?.length > 0) {
				let linkCreated = false;
				t.inputAttributes.forEach((inputAttribute) => {
					if (task?.outputAttributes?.includes(inputAttribute) && !linkCreated) {
						if (!nodes.find((n) => n.id === task?.id) || !nodes.find((n) => n.id === t?.id)) return;
						let link = createMatchesLink(task.id, t.id);
						if (link) links.push(link);
						linkCreated = true;
					}
				});
			}
		});
	});

	return { nodes, links };
}
export function dataobjectOnlyGraph(processId: string): Graph {
	if (!processesStore || !graphStore || !symbolsStore) init();
	let nodes: Node[] = [];
	let links: Link[] = [];
	let process = processesStore.getById(processId);
	if (!process || !process.dataobject) return { nodes, links };
	let dataobject: ConceptDataobject | undefined = graphStore.concept.dataobject.byId(process.dataobject);
	if (!dataobject) return { nodes, links };
	nodes.push({
		id: dataobject.id || "",
		shape: {
			type: "data-object",
			scale: 1,
			url: graphlyTemplatesStore.dataobjectShape,
		},
		x: 0,
		y: 0,
		payload: {
			title: symbolsStore.term({ id: dataobject.refersTo || "", terminology: dataobject.terminology || {} }),
			tags: composedAttributes(dataobject.id).map((composedById: any) =>
				symbolsStore.term({
					id: graphStore.concept.attribute.byId(composedById)?.refersTo || "",
					terminology: graphStore.concept.attribute.byId(composedById)?.terminology || {},
				}),
			),
			type: "collection",
			color: color(dataobject),
		},
	});
	return { nodes, links };
}

export function children(dataobjectId: string, depth = 0): string[] {
	init();
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	const dataobjects = [...(dataobject.composedBy || [])];
	if (depth != 0) {
		dataobjects.forEach((d) => {
			dataobjects.push(...children(d, depth - 1).filter((c) => !dataobjects.includes(c)));
		});
	}
	return dataobjects;
}

export function composedAttributes(dataobjectId: string): string[] {
	init();
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	return [
		...(dataobject.consistsOf || []),
		...children(dataobject.id, -1)
			.map((c) => graphStore.concept.dataobject.byId(c)?.consistsOf || [])
			.flat()
			.filter((c) => c),
	];
}

function taskNode(task: TemplateTask, process: TemplateProcess): Node {
	if (!symbolsStore) init();
	let concept = graphStore.concept.task.byId(task.concept);
	let type =
		concept?.id === "question_task"
			? "question"
			: task.context?.["nta-isNotificationTask"]
			? "send"
			: concept?.id === "system_task"
			? "service"
			: "notitia";
	return {
		id: task.id,
		shape: {
			type: "task",
			scale: scale(task, process),
			url: graphlyTemplatesStore.taskShape,
		},
		...position(task, process),
		payload: {
			mode: "default",
			type: type,
			title: symbolsStore.term({ id: task.refersTo, terminology: task.terminology || {} }),
			text: markdownToTxt(symbolsStore.description({ id: task.refersTo, terminology: task.terminology || {} })),
			color: task.color || "#D4DC76",
		},
	};
}
function conditionTaskNode(task: TemplateTask, process: TemplateProcess): Node {
	if (!symbolsStore) init();
	return {
		id: task.id,
		shape: {
			type: "condition_task",
			scale: scale(task, process),
			url: graphlyTemplatesStore.genericConditionShape,
		},
		...position(task, process),
		payload: {
			color: task.color || "#D4DC76",
			text: symbolsStore.term({ id: task.refersTo, terminology: task.terminology || {} }),
		},
	};
}
function logicGateNode(task: TemplateTask, process: TemplateProcess): Node {
	return {
		id: task.id,
		shape: {
			type: task.concept,
			scale: scale(task, process),
			url: graphlyTemplatesStore.logicGateShape,
		},
		...position(task, process),
		payload: {
			color: task.color || "#a6a6a6",
			type: task.concept.split("_")[2],
		},
	};
}
function eventNode(event: TemplateEvent, process: TemplateProcess): Node {
	let concept = graphStore.concept.event.byId(event.concept);
	let type =
		concept?.origin === "knowledge" ? "notitia" : concept?.id === "create_process_event" ? "parallel" : "mail";
	return {
		id: event.id,
		shape: {
			type: "event",
			scale: scale(event, process),
			url: graphlyTemplatesStore.eventShape,
		},
		...position(event, process),
		payload: {
			// Todo: where does payload data come from?
			mode: "interrupt",
			type: type,
			alignment: "down",
			note: symbolsStore.term({ id: event.refersTo, terminology: event.terminology || {} }),
		},
	};
}

function createActivatesLink(sourceId: string, targetId: string, label: string = ""): Link | null {
	let target = graphStore.graph.nodes[targetId];
	if (!target) return null;
	return {
		id: sourceId + "_" + target.id,
		source: sourceId,
		target: target.id,
		directed: true,
		label: label ? label : "activates",
		strength: LinkStrength.Loose,
	};
}

function createMatchesLink(sourceId: string, targetId: string): Link | null {
	let target = graphStore.graph.nodes[targetId];
	if (!target) return null;
	return {
		id: sourceId + "_" + target.id + "__matchesLink",
		source: sourceId,
		target: target.id,
		type: LinkType.Dotted,
		directed: true,
		strength: LinkStrength.Loose,
	};
}

function position(
	node: ConceptDataobject | TemplateEvent | TemplateTask,
	process: TemplateProcess,
): { x: number | undefined; y: number | undefined; anchor: Anchor | undefined; spawn: Spawn | undefined } {
	if (!processesStore) init();
	const pos = processesStore.nodePositions[process.id]?.[node.id];
	if (pos) {
		return {
			x: pos.x,
			y: pos.y,
			anchor: { type: AnchorType.Soft, x: pos.x, y: pos.y },
			spawn: undefined,
		};
	} else {
		return {
			x: undefined,
			y: undefined,
			anchor: undefined,
			spawn: undefined,
		};
	}
}
function scale(
	node: ConceptDataobject | TemplateEvent | TemplateTask,
	process: TemplateProcess,
	fallback: number = 1,
): number {
	const scale = processesStore.nodePositions[process.id]?.[node.id]?.scale;
	return scale ? scale : fallback ? fallback : 1;
}

function color(concept: ConceptDataobject | undefined) {
	return concept?.color ? concept.color : "#27282B";
}
